/* Penumbrai flexible data model (client mirror of the API schema).
 *
 * The core idea that differentiates Penumbrai: nothing is hard-coded. A
 * workspace is described by ObjectDefinitions and FieldDefinitions, and every
 * customer Record carries a free-form `attributes` bag validated against those
 * definitions. Health scores, signals, segments, and playbooks all compose on
 * top of user-defined fields — no schema migration required.
 */

export type FieldType =
  | "text"
  | "longtext" // free-form / unstructured body
  | "number"
  | "currency"
  | "percent"
  | "date"
  | "select"
  | "boolean"
  | "reference"
  | "tags" // multi-value labels (e.g. extracted themes)
  | "email";

export type DataKind = "structured" | "unstructured";

export interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  isCustom: boolean;
  options?: string[]; // for select
  refObject?: string; // for reference
  description?: string;
  usedInScore?: boolean;
}

export interface ObjectDefinition {
  apiName: string; // e.g. "account", "deployment"
  label: string;
  labelPlural: string;
  icon: string; // key into icon map
  system: boolean; // built-in vs user-created
  kind: DataKind; // structured rows vs unstructured documents
  recordCount: number;
  fields: FieldDefinition[];
  samples?: DocumentSample[]; // preview docs for unstructured objects
}

/** A single unstructured item (ticket, call transcript, email, note) with the
 * enrichments Penumbrai extracts from it: sentiment, themes, a summary. These
 * feed the same health scores and signals as structured fields. */
export interface DocumentSample {
  id: string;
  title: string;
  source: string; // Zendesk, Gong, Gmail, …
  accountName?: string;
  at: string;
  sentiment: "positive" | "neutral" | "negative";
  themes: string[];
  summary: string;
  excerpt: string;
}

/** A connected (or available) data source. */
export interface Integration {
  id: string;
  name: string;
  category: "CRM" | "Warehouse" | "Product analytics" | "Support" | "Billing" | "Conversations" | "Surveys" | "Collaboration" | "Reverse ETL";
  blurb: string;
  status: "connected" | "available";
  direction: "inbound" | "outbound" | "bidirectional";
  dataType: DataKind | "both";
  syncs: string[]; // object apiNames it maps into
  color: string;
  lastSync?: string;
  recordsSynced?: number;
}

export type HealthBand = "good" | "watch" | "risk";
export type LifecycleStage =
  | "Onboarding"
  | "Adopting"
  | "Established"
  | "At Risk"
  | "Renewing";

export interface ScoreFactor {
  label: string;
  weight: number; // 0..1
  value: number; // 0..100 contribution
  direction: "up" | "down" | "flat";
}

export interface Contact {
  name: string;
  title: string;
  role: "Champion" | "Economic Buyer" | "User" | "Detractor" | "Unknown";
  email: string;
  sentiment: "positive" | "neutral" | "negative";
}

export interface TimelineEvent {
  id: string;
  at: string; // ISO
  kind: "signal" | "note" | "play" | "meeting" | "renewal" | "milestone";
  band?: HealthBand;
  title: string;
  detail?: string;
}

/** An Account record — the primary object. Custom fields live in `attributes`. */
export interface Account {
  id: string;
  name: string;
  domain: string;
  industry: string;
  region: string;
  ownerId: string;
  arr: number;
  seats: number;
  seatsActive: number;
  lifecycleStage: LifecycleStage;
  health: number; // 0..100
  healthBand: HealthBand;
  healthTrend: number[]; // last 12 weekly points
  renewalDate: string; // ISO
  startDate: string;
  scoreFactors: ScoreFactor[];
  contacts: Contact[];
  timeline: TimelineEvent[];
  openSignals: number;
  expansionPotential: number; // 0..100
  attributes: Record<string, string | number | boolean>;
}

export type SignalBand = "risk" | "watch" | "positive";
export interface Signal {
  id: string;
  accountId: string;
  accountName: string;
  band: SignalBand;
  title: string;
  detail: string;
  source: string; // "Product", "Support", "Billing", "CRM", ...
  impact: number; // health delta, signed
  at: string;
  status: "open" | "acknowledged" | "resolved";
  suggestedPlaybook?: string;
}

export type PlaybookStepKind = "trigger" | "task" | "email" | "meeting" | "wait" | "branch" | "alert";
export interface PlaybookStep {
  id: string;
  label: string;
  detail?: string;
  kind: PlaybookStepKind;
  owner?: string;
  durationDays?: number;
  offsetDays?: number;
  dependsOn?: string[];
  taskTypeId?: string;
  assignee?: Assignee;
}
export type PlaybookScope = "account" | "relationship" | "both";
export interface Playbook {
  id: string;
  name: string;
  category: "Risk" | "Onboarding" | "Adoption" | "Expansion" | "Renewal";
  description: string;
  trigger: string;
  enabled: boolean;
  activeRuns: number;
  completedRuns: number;
  successRate: number; // 0..100
  steps: PlaybookStep[];
  scope?: PlaybookScope;
}

export type RunStepStatus = "pending" | "done" | "skipped";
export interface PlaybookRunStep {
  id: string;
  templateStepId?: string;
  label: string; kind: string; status: RunStepStatus; taskId?: string;
  dependsOn: string[];
  durationDays: number;
  startDate: string; dueDate: string;
  earliestStart: number; earliestFinish: number;
  latestStart: number; latestFinish: number;
  slack: number; critical: boolean;
}
export interface PlaybookRun {
  id: string; playbookId: string; playbookName: string; accountId: string; accountName: string;
  status: "active" | "completed" | "stopped"; steps: PlaybookRunStep[]; startedAt: string; completedAt?: string;
  scopeKind?: "account" | "relationship";
  scopeId?: string; scopeName?: string; planStartDate?: string;
}

export interface User {
  id: string;
  name: string;
  initials: string;
  role: string;
  color: string;
  booksArr: number;
  accounts: number;
  groupId?: string;
}

/* A permission group bundles capabilities that gate nav sections + config
 * routes. system groups (Admin/Manager/CSM) can't be deleted. */
export interface PermissionGroup {
  id: string;
  name: string;
  description?: string;
  capabilities: string[];
  system?: boolean;
}

/* Roles held on an account (or relationship) and used as task assignees, so a
 * task can target "the CSM of this account" and resolve to whoever holds it. */
export type RoleMapping =
  | { type: "manual" }
  | { type: "account_field"; field: string }            // an account field, e.g. CRM owner
  | { type: "contact_role"; contactRole: string }       // a contact/person by relationship role
  | { type: "record_ref"; object: string; matchField: string; holderField: string }; // a user on a related custom object
export interface RoleDef {
  key: string;
  label: string;
  scope: "account" | "relationship";
  description?: string;
  mapping?: RoleMapping;
  system?: boolean;
}
export interface Entitlements {
  roleLimit: number;
  agentLimit: number;
  automationLimit: number;
  aiRunsLimit: number;
  aiRunsUsed: number;
}
export interface AccountTeam {
  accountId: string;
  roles: Record<string, string>; // roleKey → userId
}
export type Assignee =
  | { kind: "user"; userId: string }
  | { kind: "role"; role: string };

export type TaskStatus = "todo" | "doing" | "done";
export type TaskPriority = "high" | "medium" | "low";
export type TaskSource = "playbook" | "manual" | "automation";

export interface Task {
  id: string;
  title: string;
  description?: string;
  accountId: string;
  accountName: string;
  assignee: Assignee;
  ownerId: string; // resolved user id (from assignee)
  due: string;
  status: TaskStatus;
  source: TaskSource;
  priority: TaskPriority;
  automationId?: string;
  typeId?: string;
  fields?: Record<string, TaskFieldValue>;
  createdAt: string;
  /** Annotated when the task transitions to done: who marked it complete and
   *  when. The task keeps its original owner/assignee. */
  completedBy?: string;
  completedAt?: string;
}

/* Task Types — reusable templates whose required fields gate completion. */
export type TaskFieldKind = "text" | "number" | "select" | "date" | "checkbox" | "account";
export type TaskFieldValue = string | number | boolean;
export interface TaskField {
  key: string; label: string; kind: TaskFieldKind; required: boolean;
  options?: string[]; help?: string;
}
export interface TaskType {
  id: string; name: string; description?: string; icon?: string;
  fields: TaskField[]; system?: boolean;
}

export type AutomationTriggerType = "health_below" | "renewal_within" | "health_band_is" | "signal_created";
export interface AutomationTrigger {
  type: AutomationTriggerType;
  params?: Record<string, string | number>;
}
export type ConditionOp = "lt" | "lte" | "gt" | "gte" | "eq" | "neq" | "contains";
export interface AutomationCondition {
  field: string;
  op: ConditionOp;
  value: string | number | boolean;
}
export interface AutomationAction {
  type: "create_task";
  title: string;
  assignee: Assignee;
  priority: TaskPriority;
  dueInDays: number;
  /** Optional task type stamped on the created task — gates its completion. */
  taskTypeId?: string;
}
export interface Automation {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  conditions: AutomationCondition[];
  actions: AutomationAction[];
  createdAt: string;
  lastRunAt?: string;
  createdTaskCount: number;
}

/* Risk Agents — a visual Watch → Detect → Act flow over the data. */
export type SourceRef = string;
export interface RiskSourceNode { id: string; kind: "source"; source: SourceRef; }
export type AiIndicator = "negative_sentiment" | "competitor_mention" | "sponsor_churn" | "escalation" | "churn_language" | "adoption_risk";
export interface RiskRuleDetector { id: string; kind: "detector"; mode: "rule"; label: string; conditions: AutomationCondition[]; band: SignalBand; }
export interface RiskAiDetector { id: string; kind: "detector"; mode: "ai"; label: string; indicator: AiIndicator; minConfidence: number; band: SignalBand; }
export type RiskDetectorNode = RiskRuleDetector | RiskAiDetector;
export type RiskActionNode =
  | { id: string; kind: "action"; type: "raise_signal"; title: string }
  | { id: string; kind: "action"; type: "create_task"; title: string; assignee: Assignee; priority: TaskPriority; dueInDays: number }
  | { id: string; kind: "action"; type: "notify"; target: Assignee; message: string };
export type RiskNode = RiskSourceNode | RiskDetectorNode | RiskActionNode;
export interface RiskEdge { from: string; to: string; }
export interface RiskAgent {
  id: string; name: string; description?: string; enabled: boolean;
  nodes: RiskNode[]; edges: RiskEdge[];
  createdAt: string; lastRunAt?: string; findingsCount: number; signalsRaised: number;
}
export interface RiskFindingAction { type: RiskActionNode["type"]; summary: string; }
export interface RiskFinding {
  agentId: string; accountId: string; accountName: string;
  detectorId: string; detectorLabel: string; band: SignalBand;
  reason: string; confidence: number; actions: RiskFindingAction[];
}

export type ObjectiveStatus = "todo" | "in_progress" | "done" | "blocked";
export type PlanStatus = "draft" | "on_track" | "at_risk" | "achieved";
export interface Objective {
  id: string; title: string; detail?: string; status: ObjectiveStatus;
  owner?: Assignee; dueDate?: string; metric?: string;
}
export interface SuccessPlan {
  id: string; accountId: string; accountName: string; title: string; outcome: string;
  status: PlanStatus; objectives: Objective[]; targetDate?: string; createdAt: string; updatedAt: string;
}

export type ForecastCategory = "commit" | "likely" | "at_risk" | "churn";
export type RenewalStage = "not_started" | "in_progress" | "proposal_sent" | "closed_won" | "closed_lost";
export interface RenewalOpportunity {
  id: string; accountId: string; accountName: string; arr: number; renewalDate: string;
  category: ForecastCategory; forecastAmount: number; expansionAmount: number;
  stage: RenewalStage; ownerId?: string; notes?: string; updatedAt: string;
}

export type NotificationKind = "assignment" | "signal" | "agent" | "playbook" | "mention" | "system";
export interface Notification {
  id: string; kind: NotificationKind; title: string; body?: string;
  accountId?: string; link?: string; read: boolean; at: string;
}

export interface ApiKey {
  id: string; name: string; prefix: string; last4: string;
  createdAt: string; lastUsedAt?: string; revokedAt?: string;
}
export type WebhookEvent = "account.health_changed" | "signal.created" | "task.created" | "renewal.updated" | "survey.response";
export const WEBHOOK_EVENTS: WebhookEvent[] = ["account.health_changed", "signal.created", "task.created", "renewal.updated", "survey.response"];
export interface WebhookEndpoint {
  id: string; url: string; events: WebhookEvent[]; active: boolean; secretLast4: string; createdAt: string;
}
export interface WebhookDelivery {
  id: string; endpointId: string; event: WebhookEvent; status: "success" | "failed"; responseCode: number; at: string;
}

export interface QbrDeck {
  accountId: string; accountName: string; ownerName: string; period: string; generatedAt: string;
  headline: { arr: number; health: number; healthBand: HealthBand; healthDelta: number; renewalDate: string; lifecycleStage: string };
  adoption: { seats: number; seatsActive: number; utilization: number };
  scoreFactors: { label: string; value: number; direction: string }[];
  nps: { score: number | null; responses: number; lastComment?: string };
  renewal?: { date: string; arr: number; category: ForecastCategory; forecastAmount: number; expansionAmount: number };
  wins: string[]; risks: string[]; nextSteps: string[];
}

export type LayoutEntity = "account" | "project" | "relationship";
export interface LayoutSection { id: string; widget: string; span: "full" | "half"; enabled: boolean; }
export interface Layout { id: string; entity: LayoutEntity; name: string; sections: LayoutSection[]; }

export interface PortalShare {
  id: string; token: string; accountId: string; accountName: string; createdAt: string; revokedAt?: string;
}
export interface PortalView {
  accountName: string; csmName: string; generatedAt: string;
  status: { band: HealthBand; label: string };
  adoption: { seats: number; seatsActive: number; utilization: number };
  successPlan?: { title: string; outcome: string; objectives: { title: string; status: string }[] };
  renewalDate?: string;
  nps: number | null;
  wins: string[];
  focusAreas: string[];
}

export type SyncStatus = "success" | "partial" | "failed";
export interface SyncRun {
  id: string; connectorKey: string; connectorLabel: string; object: string;
  created: number; failed: number; status: SyncStatus; message?: string; at: string;
}
export interface Connector { key: string; label: string; object: string; kind: "sample" | "http_json"; }

export interface CsmScorecard {
  userId: string; name: string; initials: string; color: string; role: string;
  accounts: number; bookArr: number; atRiskArr: number;
  health: { good: number; watch: number; risk: number }; avgHealth: number;
  openTasks: number; overdueTasks: number;
  renewals90: number; renewalArr90: number; forecastArr90: number;
  nps: number | null; npsResponses: number;
}

export type SurveyType = "nps" | "csat" | "ces";
export type SurveyStatus = "draft" | "active" | "closed";
export interface Survey {
  id: string; name: string; type: SurveyType; question: string; status: SurveyStatus; createdAt: string;
}
export interface SurveyResponse {
  id: string; surveyId: string; accountId: string; accountName: string; respondent?: string; score: number; comment?: string; at: string;
}

export interface SegmentFilter {
  field: string;
  op: string;
  value: string;
}
export interface Segment {
  id: string;
  name: string;
  description: string;
  color: string;
  filters: SegmentFilter[];
  count: number;
  arr: number;
}

/** A row of a custom/flexible object — values validated against its fields. */
export interface GenericRecord {
  id: string;
  object: string;
  attributes: Record<string, string | number | boolean>;
}

export interface Workspace {
  name: string;
  currentUserId: string;
  users: User[];
  objects: ObjectDefinition[];
  accounts: Account[];
  signals: Signal[];
  playbooks: Playbook[];
  tasks: Task[];
  roles: RoleDef[];
  teams: AccountTeam[];
  automations: Automation[];
  riskAgents: RiskAgent[];
  successPlans: SuccessPlan[];
  playbookRuns: PlaybookRun[];
  notifications: Notification[];
  renewals: RenewalOpportunity[];
  surveys: Survey[];
  surveyResponses: SurveyResponse[];
  apiKeys: ApiKey[];
  webhooks: WebhookEndpoint[];
  webhookDeliveries: WebhookDelivery[];
  syncRuns: SyncRun[];
  portalShares: PortalShare[];
  layouts: Layout[];
  healthModel: HealthModel;
  notes: Note[];
  chatChannels: ChatChannel[];
  chatDeliveries: ChatDelivery[];
  taskTypes: TaskType[];
  permissionGroups: PermissionGroup[];
  entitlements: Entitlements;
  segments: Segment[];
  integrations: Integration[];
  /** Rows for non-account objects (deployment, conversation, …), keyed by apiName. */
  records: Record<string, GenericRecord[]>;
}

/* Health Score Designer — a no-code weighting over the shared account factors. */
export interface HealthFactorWeight { label: string; weight: number; enabled: boolean; }
export interface HealthModel { factors: HealthFactorWeight[]; }

/* A CSM note on the activity timeline. `ai` marks a Claude-drafted note. */
export interface Note {
  id: string; accountId: string; authorId: string; authorName: string;
  body: string; at: string; ai: boolean;
}

/* Slack / Teams delivery. The incoming-webhook URL is stored server-side; the
 * client only ever sees the host. */
export type ChatPlatform = "slack" | "teams";
export type ChatEvent = "signal.risk" | "renewal.updated" | "churn.high" | "digest";
export interface ChatChannel {
  id: string; platform: ChatPlatform; name: string; urlHost: string;
  events: ChatEvent[]; active: boolean; createdAt: string; lastDeliveryAt?: string;
}
export interface ChatDelivery {
  id: string; channelId: string; channelName: string; platform: ChatPlatform;
  event: ChatEvent; title: string; status: "success" | "failed"; responseCode: number; at: string;
}
